import React, { createContext, useEffect, useState, useMemo, useCallback } from "react";
import { apiFetch } from "../../core/utils/api";

export const AdminAuthContext = createContext();

export const AdminAuthProvider = ({ children }) => {
  const [adminAuth, setAdminAuth] = useState({
    isAuthenticated: false,
    username: null,
    isChecking: true,
  });

  // Hydrate admin auth state from server session once on app start
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        // suppressUnauthorizedEvent: this is an admin-specific session probe.
        // A 401 here must NOT fire the global user-auth "unauthorised" event,
        // which would incorrectly clear the regular user's auth state.
        const res = await apiFetch("/api/admin/check/", {
          credentials: "include",
          suppressUnauthorizedEvent: true,
        });
        if (mounted && res.ok) {
          const data = await res.json();
          // Backend returns: {is_admin: bool, expired: bool, username: str|null}
          if (data.is_admin && !data.expired) {
            setAdminAuth({
              isAuthenticated: true,
              username: data.username,
              isChecking: false,
            });
          } else {
            setAdminAuth({
              isAuthenticated: false,
              username: null,
              isChecking: false,
            });
          }
        } else if (mounted) {
          setAdminAuth({
            isAuthenticated: false,
            username: null,
            isChecking: false,
          });
        }
      } catch (err) {
        // Network error or unauthenticated → leave as unauthenticated
        if (mounted) {
          setAdminAuth({
            isAuthenticated: false,
            username: null,
            isChecking: false,
          });
        }
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  // If any admin API call returns 401, treat it as session-expired and force re-login.
  useEffect(() => {
    const handler = () => {
      setAdminAuth({
        isAuthenticated: false,
        username: null,
        isChecking: false,
      });
    };
    window.addEventListener("admin-auth-expired", handler);
    return () => window.removeEventListener("admin-auth-expired", handler);
  }, []);

  const adminLogin = useCallback(async (username, password) => {
    try {
      const res = await apiFetch("/api/admin/login/", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ username, password }),
      });

      if (res.ok) {
        const data = await res.json();
        setAdminAuth({
          isAuthenticated: true,
          username: data.username,
          isChecking: false,
        });
        return { success: true };
      } else {
        const errorData = await res.json();
        throw new Error(errorData.error || "Invalid credentials");
      }
    } catch (err) {
      throw err;
    }
  }, []);

  const adminLogout = useCallback(async () => {
    try {
      await apiFetch("/api/admin/logout/", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      });
    } catch (err) {
      /* eslint-disable no-console */
      console.error("Admin logout error:", err);
    } finally {
      // Always clear admin auth state
      setAdminAuth({
        isAuthenticated: false,
        username: null,
        isChecking: false,
      });
    }
  }, []);

  // Memoize the context value to prevent unnecessary re-renders
  const value = useMemo(
    () => ({
      adminAuth,
      adminLogin,
      adminLogout,
    }),
    [adminAuth, adminLogin, adminLogout]
  );

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
};

